import React from 'react';
import { AreaOfStudy } from '../data/areaOfStudyData';
import { ShieldCheckIcon } from 'lucide-react';
import { programOptions, degreeLevelOptions } from '../data/filterOptions';
interface HeroSectionProps {
  areaData: AreaOfStudy;
  selectedProgram?: number;
  degreeLevel?: number;
  selectedAreaId?: number;
  t1Override?: string | null;
}
export function HeroSection({
  areaData,
  selectedProgram,
  degreeLevel,
  selectedAreaId,
  t1Override
}: HeroSectionProps) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString('default', {
    month: 'short'
  });
  // Get the program name from the selected program
  const getProgramName = () => {
    if (!selectedProgram) return null;
    const areaId = Math.floor(selectedProgram / 100);
    const programs = programOptions[areaId];
    if (!programs) return null;
    const program = programs.find(p => p.value === selectedProgram);
    return program?.label || null;
  };
  const programName = getProgramName();
  // Capitalize first letter of each word (except small words like "for", "in", "of", "a", "an")
  const capitalizeHeadline = (str: string) => {
    const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', 'or']);
    return str.split(' ').map((word, i) => {
      if (i === 0 || !smallWords.has(word.toLowerCase())) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    }).join(' ');
  };
  // Degree-level label map
  const degreeLabelMap: Record<number, string> = {
    1: 'Certificate',
    2: 'Associates Degree',
    3: "Bachelor's Degree",
    4: "Master's Degree"
  };
  const hasSpecificDegree = degreeLevel && degreeLevel !== 5 && degreeLabelMap[degreeLevel];
  const degreeLabel = hasSpecificDegree ? degreeLabelMap[degreeLevel!] : null;
  // Generate dynamic headline based on degree level and program selection
  const getHeadline = () => {
    const isGeneralArea = selectedAreaId === 9;
    const isGeneralProgram = !programName || selectedProgram === 900 || programName === 'General/Undecided';
    // Case 1: Specific degree + specific program (not general)
    if (hasSpecificDegree && programName && !isGeneralProgram && !(programName.startsWith('General ') || programName.includes('All '))) {
      let headline = `Best Online ${degreeLabel} in ${programName} of ${currentYear}`;
      if (t1Override) {
        headline = headline.replace('Best Online', t1Override.trim());
      }
      return headline;
    }
    // Case 2: Specific degree + general area (from Degree Options dropdown or filter)
    if (hasSpecificDegree && isGeneralArea) {
      const article = degreeLevel === 2 ? 'An' : 'A';
      let headline = `Best Colleges for ${article} ${degreeLabel} Program in ${currentYear}`;
      if (t1Override) {
        headline = headline.replace('Best Colleges for', t1Override.trim());
      }
      return headline;
    }
    // Case 3: Specific degree + specific area (not general), but general/all program
    if (hasSpecificDegree && !isGeneralArea) {
      let headline = `Best Online ${degreeLabel} in ${areaData.name} of ${currentYear}`;
      if (t1Override) {
        headline = headline.replace('Best Online', t1Override.trim());
      }
      return headline;
    }
    // Case 4: No specific degree selected — use existing logic
    let headline = '';
    const isDefaultLanding = isGeneralArea && isGeneralProgram;
    if (isDefaultLanding) {
      headline = `Top Accredited Degree Programs of ${currentYear}`;
    } else if (!programName || programName.startsWith('General ') || programName.includes('All ')) {
      headline = `Best Colleges for an Online ${areaData.name} Degree of ${currentYear}`;
    } else if (selectedProgram === 900 || programName === 'General/Undecided') {
      headline = `Top Accredited Degree Programs of ${currentYear}`;
    } else {
      headline = `Best Colleges for an Online ${programName} Degree of ${currentYear}`;
    }
    if (t1Override) {
      headline = headline.replace('Top Accredited', t1Override.trim());
    }
    return headline;
  };
  return <section className="w-full bg-[#1a2332] text-white py-8 md:py-12 px-4 relative overflow-hidden" data-id="element-490" data-mp="2q20nedgwixv1">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-491" data-mp="2c2r8dh4fm9sn"></div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-492" data-mp="2h1c5h7oe0dcv">
        <div className="max-w-3xl lg:max-w-5xl" data-id="element-493" data-mp="34qenigmxo87w">
          <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-200 mb-3 border border-white/20" data-id="element-494" data-mp="34srbpbjabete">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-495" data-mp="2v0bvojrjt0b5" />
            <span data-id="element-496" data-mp="2zzeqlbdtdq5e">
              Last Updated: {currentMonth} {currentYear}
            </span>
          </div>

          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2" data-id="element-497" data-mp="3np91hnekp4n0">
            {capitalizeHeadline(getHeadline())}
          </h1>

          <p className="text-sm md:text-base text-gray-300 max-w-xl" data-id="element-498" data-mp="3sibzc7qdv090">
            Compare top accredited online programs and find the right fit for
            you.
          </p>
        </div>
      </div>
    </section>;
}